'use client';

import dynamic from 'next/dynamic';
import {useCallback, useEffect, useState} from 'react';
import {ChevronDown, X} from 'lucide-react';
import type {OnMount} from '@monaco-editor/react';
import {Spacer} from '@repo/ui/spacer';
import type {CodeRunResponse, Language, Question} from 'iq-questions/types';
import {runCode} from 'iq-questions/api';
import {RunResults} from 'iq-questions/components/run_results';
import {EditorToolbar} from 'common/components/editor_toolbar';
import {EditorError} from 'common/components/editor_error';

const MonacoEditor = dynamic(() => import('@monaco-editor/react').then((mod) => mod.default), {
  ssr: false,
  loading: () => <div className='border border-iq-grey-light rounded' style={{height: '20vh'}} />,
});

const THEME_OPTIONS = [
  {value: 'light', label: 'Light'},
  {value: 'vs-dark', label: 'Dark'},
];

interface CodeEditorProps {
  data: Question;
  viewUrl?: string;
}

export function CodeEditor({data, viewUrl}: CodeEditorProps) {
  const [selectedLanguage, setSelectedLanguage] = useState<Language | undefined>(
    data.languages?.[0],
  );
  const [value, setValue] = useState(selectedLanguage?.template ?? '');
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('editor-theme') ?? 'light';
    }
    return 'light';
  });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<CodeRunResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem('editor-theme', theme);
  }, [theme]);

  const handleLanguageChange = useCallback(
    (languageId: string) => {
      const lang = data.languages?.find((l) => String(l.language_id) === languageId);
      if (lang) {
        setSelectedLanguage(lang);
        setValue(lang.template);
        setResult(null);
        setError(null);
      }
    },
    [data.languages],
  );

  const handleRun = useCallback(async () => {
    if (!selectedLanguage) return;
    setRunning(true);
    setResult(null);
    setError(null);
    try {
      const res = await runCode({
        question_uuid: data.uuid,
        raw_code: value,
        language_version: selectedLanguage.language_version,
        is_submission: false,
      });
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setRunning(false);
    }
  }, [data.uuid, selectedLanguage, value]);

  const handleMount: OnMount = useCallback(
    (editor, monaco) => {
      editor.addAction({
        id: 'run-code',
        label: 'Run code',
        keybindings: [monaco.KeyMod.Shift | monaco.KeyCode.Enter],
        run: handleRun,
      });
    },
    [handleRun],
  );

  const languageOptions = (data.languages ?? []).map((l) => ({
    value: String(l.language_id),
    label: l.language_name,
  }));

  return (
    <div className='flex flex-col gap-(--iq-gap-sm)'>
      {/* Top toolbar */}
      <div className='flex items-center justify-between gap-(--iq-gap-md)'>
        <div className='flex items-center gap-x-2'>
          <SelectPill
            value={String(selectedLanguage?.language_id ?? '')}
            options={languageOptions}
            onChange={handleLanguageChange}
          />
          <SelectPill value={theme} options={THEME_OPTIONS} onChange={setTheme} />
        </div>
        <button
          className='flex items-center gap-1.5 text-xs text-iq-grey-medium border border-iq-grey-light rounded px-3 py-1.5 hover:bg-gray-50 transition-colors disabled:opacity-50'
          onClick={() => {
            setValue(selectedLanguage?.template ?? '');
            setResult(null);
            setError(null);
          }}
          disabled={running}>
          <X className='w-3 h-3' />
          Clean editor
        </button>
      </div>
      <Spacer />

      {/* Editor */}
      <div className='border border-iq-grey-light rounded overflow-hidden'>
        <MonacoEditor
          height='20vh'
          language={selectedLanguage?.highlight_code ?? 'plaintext'}
          theme={theme}
          value={value}
          onChange={(v) => setValue(v ?? '')}
          onMount={handleMount}
          options={{
            scrollBeyondLastLine: false,
            minimap: {enabled: false},
            fontSize: 14,
            readOnly: running,
          }}
        />
      </div>
      <Spacer />

      {/* Bottom toolbar */}
      <EditorToolbar
        onClick={handleRun}
        disabled={running || !selectedLanguage}
        label='Run code'
        runningLabel='Running…'
        running={running}
        viewUrl={viewUrl}
        hint='Use Shift + Enter to run'
      />

      {/* Error */}
      <EditorError error={error} />

      {/* Results */}
      {result && <RunResults result={result} />}
    </div>
  );
}

function SelectPill({
  value,
  options,
  onChange,
}: {
  value: string;
  options: {value: string; label: string}[];
  onChange: (v: string) => void;
}) {
  return (
    <div className='relative inline-flex items-center'>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className='appearance-none text-xs border border-iq-grey-light rounded px-3 py-1.5 pr-7 bg-white text-iq-grey-dark hover:border-iq-grey-medium transition-colors cursor-pointer focus:outline-none'>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown className='pointer-events-none absolute right-2 w-3 h-3 text-iq-grey-medium' />
    </div>
  );
}
